<template>
  <div class="relative w-full h-full flex flex-col pl-1 pr-0.5 pt-1 pb-0.5 overflow-hidden">
    <div class="w-full flex items-center justify-between px-3 pt-3">
      <BaseText type="heading" size="md" class="text-text-muted">
        {{ log.method }} {{ log.full_endpoint }}
      </BaseText>
      <button class="text-icon-normal transition-colors duration-100 hover:text-icon-muted z-50"
      @click="$emit('toggleDetailsMaximized')">
        <MaximizeDetailsIcon :expanded="detailsMaximized" />
      </button>
    </div>
    <div class="relative w-full flex mt-4 items-start">
      <div ref="metadataGrid" class="grid gap-y-2 gap-x-3 px-3 flex-shrink-0" style="grid-template-columns: 180px 1fr"
      :style="{width: `${detailsBaseWidth}px`}">
        <!-- Response Status -->
        <BaseText type="body" size="sm" class="text-text-normal">
          Status
        </BaseText>
        <div class="colored-label success w-max">
          {{ log.response_status }} {{ getHttpStatusName(log.response_status) }}
        </div>
        <!-- Trace ID -->
        <BaseText type="body" size="sm" class="text-text-normal">
          Trace ID
        </BaseText>
        <BaseText type="body" size="sm" class="text-text-normal">
          {{ log.trace_id }}
        </BaseText>
        <!-- Timestamp -->
        <BaseText type="body" size="sm" class="text-text-normal">
          Timestamp
        </BaseText>
        <BaseText type="body" size="sm" class="text-text-normal">
          {{ log.timestamp }}
        </BaseText>
        <!-- IP Address -->
        <BaseText type="body" size="sm" class="text-text-normal">
          IP Address
        </BaseText>
        <BaseText type="body" size="sm" class="text-text-normal">
          {{ log.ip_address }}
        </BaseText>
        <!-- Country -->
        <BaseText type="body" size="sm" class="text-text-normal">
          Country
        </BaseText>
        <BaseText type="body" size="sm" class="text-text-normal">
          {{ log.country }}
        </BaseText>
        <!-- API Product -->
        <BaseText type="body" size="sm" class="text-text-normal">
          API Product
        </BaseText>
        <BaseText type="body" size="sm" class="text-text-normal">
          {{ log.product }}
        </BaseText>
      </div>
      <!-- Query Params (Maximized View) -->
      <div class="query-params-maximized-container flex-grow relative pr-3" style="margin-right: 70px;"
      :style="{height: maximizedQueryParamsHeight}">
        <transition>
          <div v-if="detailsMaximized && queryParams.length" class="absolute w-full h-full whitespace-nowrap" 
          style="max-width: 600px; top: -39px;">
            <RequestQueryParamsGrid :queryParams="queryParams" no-divider />
          </div>
        </transition>
      </div>
      <!-- Fade Overlay -->
      <div class="fade-overlay absolute -right-1 -top-12 bottom-0 z-40" style="width: 72px" />
    </div>
    <!-- Query Params (Minimized View) -->
    <div class="query-params-container w-full overflow-hidden flex-shrink-0 px-2"
    :style="{height: queryParamsHeight}">
      <RequestQueryParamsGrid :queryParams="queryParams" />
    </div>
    <!-- Divider -->
    <div class="w-full h-0 border-t border-border-normal mt-4 ml-2" style="width: calc(100% - 16px)" />
    <!-- RESPONSE BODY -->
    <div class="w-full flex items-center justify-between px-3 mt-2 pb-2">
      <BaseText type="label" size="md" class="text-text-muted">
        Response Body
      </BaseText>
      <button class="relative group p-2 rounded-lg transition-colors duration-100 hover:bg-neutral-25 z-10 whitespace-nowrap"
      v-clipboard:copy="responseBodyString" v-clipboard:success="handleResponseBodyCopy">
        <div class="flex items-center gap-1.5" key="base-copy-button">
          <CopyIcon class="text-icon-normal transition-colors duration-100 group-hover:text-icon-muted" />
          <BaseText type="label" size="sm" class="text-text-normal transition-colors duration-100 group-hover:text-text-muted">
            Copy JSON
          </BaseText>
        </div>
      </button>
    </div>
    <div class="flex-grow relative w-full overflow-scroll scrollbar-hide">
      <!-- Loading -->
      <BaseLoadingSpinnerCircle v-if="loadingLogDetails" :width="32" :height="32" :duration="1" style="z-index: 100;" 
      class="text-icon-disabled absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
      <div class="response-body-container relative w-full h-full flex gap-0.5 overflow-scroll min-h-0">
        <!-- Line Numbering -->
        <div class="flex-shrink-0 sticky left-0 z-10">
          <pre class="pl-3 pr-1 bg-white text-text-subdued"><code>{{ lineNumbers }}</code></pre>
        </div>
        <!-- RESPONSE JSON RENDERING -->
        <div class="relative min-w-0 min-h-0 max-h-none transition-opacity duration-200"
        :class="loadingLogDetails ? 'opacity-50' : 'opacity-100'">
          <pre :class="{'pointer-events-none': prettyResponseBody}"><code v-html="responseBodyString" class="text-text-muted" /></pre>
          <pre v-if="prettyResponseBody && showHighlights" class="absolute top-0 left-0 bg-white fade-in"><code v-html="prettyResponseBody" /></pre>
          <ChunkedJsonRenderer 
            v-if="chunkedResponseBody && showHighlights" 
            class="absolute top-0 left-0"
            :chunks="chunkedResponseBody" 
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import getHttpStatusName from '../../../utils/HttpStatusNameUtils'
import RequestQueryParamsGrid from './RequestQueryParamsGrid.vue'
import ChunkedJsonRenderer from './ChunkedJsonRenderer.vue'

import CopyIcon from '../../globals/Icons/CopyIconStatic.vue'
import CheckmarkIcon from '../../globals/Icons/NewCheckmarkIcon.vue'
import MaximizeDetailsIcon from '../../globals/Icons/ApiIcons/MaximizeDetailsIcon.vue'

const MIN_LINES_FOR_CHUNKED_RENDERING = 1000
const CHUNK_SIZE = 100 // lines

export default {
  name: 'LogRequestDetails',
  components: {
    RequestQueryParamsGrid,
    ChunkedJsonRenderer,
    CopyIcon,
    CheckmarkIcon,
    MaximizeDetailsIcon
  },
  props: {
    log: {
      type: Object,
      required: true
    },
    logDetails: {
      type: Object,
      required: true
    },
    detailsMaximized: {
      type: Boolean,
      required: true
    },
    detailsBaseWidth: {
      type: Number,
      required: true
    },
    detailsAnimDur: {
      type: Number,
      required: true
    },
    loadingLogDetails: {
      type: Boolean,
      required: false
    }
  },
  data () {
    return {
      responseData: null,
      responseBodyString: null,
      prettyResponseBody: null,
      chunkedResponseBody: null,
      hideResponseBody: false,

      metadataGridHeight: 0
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.updateMetadataGridHeight()
      this.parseRawTraceData(this.logDetails, true)
    })
  },
  watch: {
    logDetails (newLogDetails) {
      this.updateMetadataGridHeight()
      this.parseRawTraceData(newLogDetails)
    }
  },
  computed: {
    queryParams () {
      return Object.entries(this.logDetails?.query_params ?? {}).flat()
    },
    queryParamsHeight () {
      const doShow = !this.detailsMaximized && this.queryParams.length
      return doShow ? `${93 + (this.queryParams.length / 2 * 33)}px` : '0px'
    },
    maximizedQueryParamsHeight () {
      const doShow = this.detailsMaximized && this.queryParams.length
      return doShow 
        ? `${Math.max(this.metadataGridHeight, 25 + (this.queryParams.length / 2 * 33))}px` 
        : `${this.metadataGridHeight}px`
    },
    numLines () {
      return this.responseBodyString?.match(/\n/g)?.length + 1 ?? 0
    },
    lineNumbers () {
      return Array.from({ length: this.numLines }, (_, i) => i + 1).join('\n')
    },
    showHighlights () {
      return !this.hideResponseBody && !this.loadingLogDetails
    }
  },
  methods: {
    parseRawTraceData (traceData, isMounted = false) {
      this.prettyResponseBody = null
      const responseData = traceData.response_body
      this.responseData = responseData

      // Format JSON for display
      const jsonString = JSON.stringify(responseData, null, 2)
      this.responseBodyString = jsonString
      
      setTimeout(() => {
        const highlightedJsonString = Prism.highlight(jsonString, Prism.languages.json, 'json')
        if (this.numLines < MIN_LINES_FOR_CHUNKED_RENDERING) {
          this.chunkedResponseBody = null
          this.prettyResponseBody = highlightedJsonString
        } else {
          // For very large responses, we need to use a more optimized rendering method
          this.prettyResponseBody = null
          this.chunkedResponseBody = chunkJsonString(highlightedJsonString, CHUNK_SIZE)
        }
      }, isMounted ? this.detailsAnimDur + 200 : 50)
    },
    getHttpStatusName (statusCode) {
      return getHttpStatusName(statusCode)
    },
    optimizeForExit () {
      this.prettyResponseBody = null
      this.chunkedResponseBody = null
    },
    optimizeForAnimation () {
      this.hideResponseBody = true
    },
    handleAnimationEnd () {
      this.hideResponseBody = false
    },
    handleResponseBodyCopy () {
      this.$showAlert({
        type: 'success',
        message: 'Copied response body JSON to clipboard'
      })
    },
    updateMetadataGridHeight () {
      this.$nextTick(() => {
        requestAnimationFrame(() => { this.metadataGridHeight = this.$refs.metadataGrid.clientHeight })
      })
    }
  }
}

const chunkJsonString = (jsonString, chunkSize) => {
  const chunks = []
  let current = []
  let start = 0
  for (let i = 0; i < jsonString.length; i++) {
    if (jsonString[i] === '\n') {
      current.push(jsonString.slice(start, i + 1))
      start = i + 1
      if (current.length === chunkSize) {
        chunks.push(current.join(''))
        current = []
      }
    }
  }
  if (start < jsonString.length) {
    current.push(jsonString.slice(start))
    chunks.push(current.join(''))
  }
  return chunks
}
</script>

<style scoped>
.colored-label {
  padding: 4px 5px 4px 5px;
  border-radius: 4px;

  font-family: Inter;
  font-size: 11px;
  font-style: normal;
  font-weight: 600;
  line-height: 12px; /* 109.091% */
}
.colored-label.success {
  background-color: #D1F4EE;
  color: #00826E;
}
.colored-label.error {
  background-color: #FFF6E0;
  color: #603A20;
}

.fade-in {
  animation: fadeIn 150ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.query-params-container {
  transition: height var(--details-anim-dur) ease-in-out;
}
.query-params-maximized-container {
  transition: height var(--details-anim-dur) ease-in-out;
}
.fade-overlay {
  background: linear-gradient(to left, white 60%, transparent);
}

.response-body-container::-webkit-scrollbar {
  height: 12px;
  width: 12px;
}
.response-body-container::-webkit-scrollbar-thumb {
  background-color: rgba(236, 239, 243, 1);
  border-radius: 10px;
  border: 3px solid #ffffff;
  cursor: pointer;
  transition: border-width 150ms ease-in-out;
}
.response-body-container::-webkit-scrollbar-thumb:hover {
  background-color: rgba(223, 225, 231, 1);
  border-width: 2px;
}

.v-enter-active, .v-leave-active {
  transition: opacity 250ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}

/* Syntax Highlighting Overrides */
:deep(.token.property) {
  color: #303546;
}
:deep(.token.punctutation) {
  color: #5E6678;
}
:deep(.token.string) {
  color: #287F6E;
}
:deep(.token.operator) {
  color: #5E6678;
}
:deep(.token.boolean) {
  color: #003E6B;
}
:deep(.token.number) {
  color: #40C4AA;
}
:deep(.token.keyword) {
  color: #003E6B;
}
</style>